import { useQuery } from '@tanstack/react-query';

import useAuthSelectors from './useAuthSelector';

import { auth0AccessToken } from 'modules/Auth/auth0/tokenBridge';

/**
 * The raw access token for code that calls the API as the member outside collectiveApi:
 * Decagon's tools use it exactly as they use the Django one, and the backend accepts an Auth0
 * token under JWT or Bearer. The Django access token first, otherwise the Auth0 one.
 *
 * The SDK answers from its memory cache and renews near expiry, so a 60s poll keeps Decagon's
 * copy current. The token is held in memory only. The query makes no collectiveApi call and runs
 * only for an Auth0 session, so it is safe in components mounted on every route.
 */
export default function useApiAccessToken(): string | undefined {
    const { token, isAuth0Session } = useAuthSelectors('token', 'isAuth0Session');
    const djangoAccess = token?.access;

    const { data } = useQuery({
        queryKey: ['auth', 'useApiAccessToken', 'auth0'],
        queryFn: () => auth0AccessToken(),
        enabled: !djangoAccess && isAuth0Session,
        staleTime: 60_000,
        refetchInterval: 60_000,
        refetchOnWindowFocus: false,
        retry: false,
    });

    return djangoAccess ?? (isAuth0Session ? data ?? undefined : undefined);
}
